import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LogOut, Menu, Search, ShoppingBag, User, X } from 'lucide-react';
import { useCartStore, cartCount } from '@/features/cart/cartStore';
import { useAuth } from '@/features/auth/AuthProvider';
import { useGoldSettings } from '@/features/catalog/goldSettings';
import { cn } from '@/lib/utils';
import { PRIMARY_NAV } from './nav';

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const items = useCartStore((s) => s.items);
  const openCart = useCartStore((s) => s.open);
  const { user, isStaff, signOut } = useAuth();
  const count = cartCount(items);
  const accountRef = useRef<HTMLDivElement>(null);
  // Warm the live Gold-desk settings cache app-wide so the cart's client price
  // estimate matches the live Gold page (the sync pricer reads this cache).
  useGoldSettings();

  // Close the account dropdown when clicking/tapping outside it. (Replaces the
  // old button onBlur close, which raced the menu-link click and made the items
  // appear unclickable — blur fired and unmounted the link before its click.)
  useEffect(() => {
    if (!accountOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setAccountOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [accountOpen]);

  return (
    <header className="fs-site-header sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="fs-site-header__inner">
        {/* Mobile menu toggle */}
        <button
          className="fs-site-header__menu-toggle lg:hidden"
          aria-label="Toggle menu"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>

        {/* Brand */}
        <Link to="/" className="fs-site-header__brand font-display text-primary glow-text">
          Firstseller
        </Link>

        {/* Primary nav (desktop) */}
        <nav className="fs-site-header__nav hidden lg:flex">
          {PRIMARY_NAV.map((group) => {
            const Icon = group.icon;
            return group.links ? (
              <div key={group.label} className="group relative">
                <button className="fs-site-header__nav-item">
                  <Icon className="h-5 w-5" />
                  {group.label}
                </button>
                <div className="invisible absolute left-0 top-full min-w-56 translate-y-2 rounded-md border border-border bg-popover p-1 opacity-0 shadow-lg transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  {group.links.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      className="block rounded px-3 py-2 text-sm text-foreground/90 hover:bg-accent hover:text-foreground"
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : (
              <NavLink
                key={group.label}
                to={group.to!}
                className={({ isActive }) =>
                  cn(
                    'fs-site-header__nav-item',
                    isActive ? 'text-primary' : 'text-foreground/90',
                  )
                }
              >
                <Icon className="h-5 w-5" />
                {group.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Tools */}
        <div className="fs-site-header__tools flex items-center gap-1">
          <Link to="/search" aria-label="Search" className="fs-site-header__icon-button">
            <Search className="h-5 w-5" />
          </Link>

          {/* Account */}
          <div className="relative" ref={accountRef}>
            {user ? (
              <>
                <button
                  aria-label="Account menu"
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  className="fs-site-header__icon-button"
                  onClick={() => setAccountOpen((v) => !v)}
                >
                  <User className="h-5 w-5" />
                </button>
                {accountOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-full z-50 min-w-52 rounded-md border border-border bg-popover p-1 shadow-lg"
                  >
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {user.email}
                    </div>
                    <AccountLink to="/account" label="Account settings" onNavigate={() => setAccountOpen(false)} />
                    <AccountLink to="/orders" label="My orders" onNavigate={() => setAccountOpen(false)} />
                    <AccountLink to="/wishlist" label="Wishlist" onNavigate={() => setAccountOpen(false)} />
                    <AccountLink to="/my-chat" label="Support chat" onNavigate={() => setAccountOpen(false)} />
                    {isStaff && (
                      <AccountLink to="/admin" label="Admin panel" onNavigate={() => setAccountOpen(false)} />
                    )}
                    <button
                      role="menuitem"
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-accent"
                      onClick={() => {
                        setAccountOpen(false);
                        void signOut();
                      }}
                    >
                      <LogOut className="h-4 w-4" /> Log out
                    </button>
                  </div>
                )}
              </>
            ) : (
              <Link to="/login" className="fs-site-header__icon-button" aria-label="Log in">
                <User className="h-5 w-5" />
              </Link>
            )}
          </div>

          {/* Cart */}
          <button
            onClick={openCart}
            aria-label={`Cart, ${count} item${count === 1 ? '' : 's'}`}
            className="fs-site-header__icon-button relative"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-xs font-bold text-primary-foreground">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="border-t border-border bg-background px-4 py-3 lg:hidden">
          {PRIMARY_NAV.flatMap((group) =>
            group.links
              ? group.links
              : [{ label: group.label, to: group.to! }],
          ).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setMobileOpen(false)}
              className="block rounded px-2 py-2 text-sm hover:bg-accent"
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}

function AccountLink({
  to,
  label,
  onNavigate,
}: {
  to: string;
  label: string;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onNavigate}
      className="block rounded px-3 py-2 text-sm hover:bg-accent"
    >
      {label}
    </Link>
  );
}
